import React from 'react';
import { ClipboardCheck, Ruler, Hammer, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

const WorkProcessSection: React.FC = () => {
  const { t } = useLanguage();

  const steps = [
    {
      icon: ClipboardCheck,
      number: '01',
      title: t('home.process.step1.title'),
      description: t('home.process.step1.desc'),
    },
    {
      icon: Ruler,
      number: '02',
      title: t('home.process.step2.title'),
      description: t('home.process.step2.desc'),
    },
    {
      icon: Hammer,
      number: '03',
      title: t('home.process.step3.title'),
      description: t('home.process.step3.desc'),
    },
    {
      icon: CheckCircle2,
      number: '04',
      title: t('home.process.step4.title'),
      description: t('home.process.step4.desc'),
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('home.process.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('home.process.subtitle')}
          </p>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-border -translate-y-1/2 z-0" />
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: index * 0.15, ease: "easeOut" }}
              >
                <motion.div
                  className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mb-4 shadow-lg relative"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <step.icon className="h-8 w-8 text-primary-foreground" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-secondary text-secondary-foreground text-xs font-bold flex items-center justify-center">
                    {step.number}
                  </span>
                </motion.div>
                <h3 className="font-semibold text-foreground text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-[200px]">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkProcessSection;
